// Schedule (Routine) UI helpers — turn a friendly cadence into a cron string, render a
// cron back into human words, and preview the next few fire times. UI-only: the preview
// is computed in the browser's local time (the common case is "my own timezone"); the
// backend sweeper is the source of truth at run time.

export type Cadence =
  | "daily"
  | "weekdays"
  | "weekly"
  | "hourly"
  | "everyNMinutes"
  | "everyNHours"
  | "custom";

export const DOW_LABELS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;

/** The browser's IANA timezone (e.g. "Asia/Karachi"), for defaulting a schedule. */
export function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

function hhmm(time: string): { h: number; m: number } {
  const [hStr, mStr] = time.split(":");
  const h = Math.min(23, Math.max(0, Number(hStr) || 0));
  const m = Math.min(59, Math.max(0, Number(mStr) || 0));
  return { h, m };
}

/** Build a 5-field cron from a friendly cadence. `dow` is 0-6 (Sun-Sat) for weekly. */
export function buildCron(input: {
  cadence: Cadence;
  time: string; // "HH:MM"
  dow?: number; // weekly
  minute?: number; // hourly (0-59)
  every?: number; // everyNMinutes / everyNHours — the interval N
  custom?: string; // custom
}): string {
  const { h, m } = hhmm(input.time);
  switch (input.cadence) {
    case "daily":
      return `${m} ${h} * * *`;
    case "weekdays":
      return `${m} ${h} * * 1-5`;
    case "weekly":
      return `${m} ${h} * * ${input.dow ?? 1}`;
    case "hourly":
      return `${input.minute ?? 0} * * * *`;
    case "everyNMinutes": {
      // Repeat every N minutes. Cron minute step maxes out at the 0-59 field, so 60+ belongs
      // in everyNHours; clamp to a sane 1-59 so a stray 0 never means "every minute of ever".
      const n = Math.min(59, Math.max(1, Math.floor(input.every ?? 5)));
      return `*/${n} * * * *`;
    }
    case "everyNHours": {
      // Repeat every N hours, firing at the chosen minute each time (":mm").
      const n = Math.min(23, Math.max(1, Math.floor(input.every ?? 1)));
      const min = Math.min(59, Math.max(0, Math.floor(input.minute ?? 0)));
      return `${min} */${n} * * *`;
    }
    case "custom":
      return (input.custom ?? "").trim();
  }
}

function fmtTime(h: number, m: number): string {
  const am = h < 12;
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${am ? "AM" : "PM"}`;
}

/** Render a 5-field cron into human words for the common cadences; falls back to the raw
 * expression for anything exotic. */
export function cronToHuman(cron: string): string {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return cron.trim() || "(no schedule)";
  const [mn, hr, dom, mon, dow] = parts as [string, string, string, string, string];
  const m = Number(mn);
  const h = Number(hr);
  const timely = Number.isInteger(m) && Number.isInteger(h);
  if (dom === "*" && mon === "*" && dow === "*") {
    // every N minutes: "*/N * * * *"
    if (hr === "*" && /^\*\/\d+$/.test(mn)) {
      const n = Number(mn.slice(2));
      return n === 1 ? "Every minute" : `Every ${n} minutes`;
    }
    // every N hours at :mm: "M */N * * *"
    if (/^\*\/\d+$/.test(hr) && Number.isInteger(m)) {
      const n = Number(hr.slice(2));
      return `Every ${n} ${n === 1 ? "hour" : "hours"} at :${String(m).padStart(2, "0")}`;
    }
  }
  if (dom === "*" && mon === "*") {
    if (hr === "*" && Number.isInteger(m)) return `Every hour at :${String(m).padStart(2, "0")}`;
    if (dow === "*" && timely) return `Every day at ${fmtTime(h, m)}`;
    if (dow === "1-5" && timely) return `Every weekday at ${fmtTime(h, m)}`;
    if (timely && /^[0-6]$/.test(dow))
      return `Every ${DOW_LABELS[Number(dow)]} at ${fmtTime(h, m)}`;
  }
  return cron.trim();
}

function matchField(field: string, value: number, min: number, max: number): boolean {
  if (field === "*") return true;
  for (const part of field.split(",")) {
    const [range = "*", step] = part.split("/");
    const st = step ? Number(step) : 1;
    let lo: number;
    let hi: number;
    if (range === "*") {
      lo = min;
      hi = max;
    } else if (range.includes("-")) {
      const [a, b] = range.split("-");
      lo = Number(a);
      hi = Number(b);
    } else {
      lo = Number(range);
      hi = lo;
    }
    if (Number.isNaN(lo) || Number.isNaN(hi)) continue;
    for (let v = lo; v <= hi; v += st) if (v === value) return true;
  }
  return false;
}

/**
 * The next `count` fire times for a cron, in the browser's LOCAL time — for a friendly
 * preview in the builder. Returns formatted strings like "Tomorrow, 8:00 AM". Empty if
 * the cron is invalid or nothing matches within the lookahead.
 */
export function nextLocalRuns(cron: string, count: number, from: Date): string[] {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return [];
  const [mn, hr, dom, mon, dow] = parts as [string, string, string, string, string];
  const out: string[] = [];
  const cursor = new Date(from.getTime());
  cursor.setSeconds(0, 0);
  cursor.setMinutes(cursor.getMinutes() + 1);
  const CAP = 60 * 24 * 400; // ~400 days of minutes
  for (let i = 0; i < CAP && out.length < count; i += 1) {
    if (
      matchField(mn, cursor.getMinutes(), 0, 59) &&
      matchField(hr, cursor.getHours(), 0, 23) &&
      matchField(dom, cursor.getDate(), 1, 31) &&
      matchField(mon, cursor.getMonth() + 1, 1, 12) &&
      matchField(dow, cursor.getDay(), 0, 6)
    ) {
      out.push(formatRunLabel(cursor, from));
    }
    cursor.setMinutes(cursor.getMinutes() + 1);
  }
  return out;
}

function formatRunLabel(when: Date, now: Date): string {
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const dayDiff = Math.round((startOfDay(when) - startOfDay(now)) / 86_400_000);
  const time = fmtTime(when.getHours(), when.getMinutes());
  if (dayDiff === 0) return `Today, ${time}`;
  if (dayDiff === 1) return `Tomorrow, ${time}`;
  if (dayDiff < 7) return `${DOW_LABELS[when.getDay()]}, ${time}`;
  return `${when.toLocaleDateString(undefined, { month: "short", day: "numeric" })}, ${time}`;
}
